import React, { useState, useEffect } from "react";
import { Card, Form, Input, Button, Row, Col, message } from "antd";
import { UploadOutlined, BgColorsOutlined, ReloadOutlined } from "@ant-design/icons";
import styles from './portalManagment.module.scss';
import { errorToast, successToast } from "../../api/Util";
import { useDispatch, useSelector } from "react-redux";
import { deleteImageFromServer, getWhiteLabelCustomerInfo, updateWhiteLabelSettings } from "../../services/whiteLabelService";
import ImageUpload from "../ImageUpload/imageUpload";
import "./PortalManagmentStyle.scss";
import { sub } from "date-fns";
import ColorPickerModal from "../common/ColorPickerModal/ColorPickerModal";
import {applyMiddleware, createStore} from "redux";
import reduxThunk from "redux-thunk";
import { FETCH_SUB_DOMAIN_DATA, FETCHING_SUB_DOMAIN_DATA } from "../../actions/types";
import reducers from "../../reducers";
import { axiosClient } from "../../api/httpClient";


const initialState = {
  loginPageLogo: null,
  loginPageTagLine: "",
  sideBarLogo: null,
  favIcon: null,
  themeColor: "#ab322d",
  secondaryColor: "#0693E3"
};

const getBase64 = (file, cb) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => cb(reader.result);
};

const PortalManagementForm = () => {
  const store = createStore(reducers, {}, applyMiddleware(reduxThunk));
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState(initialState);
  const [originalValues, setOriginalValues] = useState(initialState);
  const [primaryColor, setPrimaryColor] = useState(initialState.themeColor);
  const [secondaryColor, setSecondaryColor] = useState(initialState.secondaryColor);
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [showSecondaryPicker, setShowSecondaryPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const props = useSelector((state) => state)
  const subDomainData = props.subDomain.subDomainData;

  useEffect(() => {
    fetchPortalSettings();
  }, []);

  const fetchPortalSettings = async () => {
    setDataLoading(true);
    try {
      const data = subDomainData || {};
      const settingsData = {
        loginPageLogo: data.homepage_logo || null,
        loginPageTagLine: data.homepage_logo_tagline || "",
        sideBarLogo: data.sidebar_logo || null,
        favIcon: data.fav_icon || null,
        themeColor: data.primary_color || "#ab322d",
        secondaryColor: data.primary_text_color || "#0693E3"
      };

      setFormValues(settingsData);
      setOriginalValues(settingsData);
      setPrimaryColor(settingsData.themeColor);
      setSecondaryColor(settingsData.secondaryColor);

      form.setFieldsValue(settingsData);

    } catch (error) {
      console.error('Error fetching portal settings:', error);
      errorToast('Failed to load portal settings');
    } finally {
      setDataLoading(false);
    }
  };

  const handleImageChange = (fieldName, imageData) => {
    setFormValues(prev => ({
      ...prev,
      [fieldName]: imageData
    }));
    form.setFieldValue(fieldName, imageData);
  };

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        loginPageLogo: values.loginPageLogo || subDomainData?.homepage_logo,
        loginPageTagLine: values.loginPageTagLine,
        homepageYTTagLine: values.homepageYTTagLine || subDomainData?.homepage_ythelper_tagline || '',
        homepageYTLogo: values.homepageYTLogo || subDomainData?.homepage_ythelper_logo,
        profileLogo: values.profileLogo || subDomainData?.profile_logo,
        sideBarLogo: values.sideBarLogo || subDomainData?.sidebar_logo,
        favIcon: values.favIcon || subDomainData?.fav_icon,
        themeColor: values.themeColor || primaryColor,
        secondaryColor: values.secondaryColor || secondaryColor,
        websiteTypeId: subDomainData?.parent_id || 3,
        isActive: subDomainData?.is_active ?? 1,
        repurposingVideosCount: subDomainData?.repurposing_videos_count || 0,
        subDomain: subDomainData?.url || '', // Ensure it ends with `/` on backend
      };
      const response = await updateWhiteLabelSettings(subDomainData.id, payload);

      if (response.data.message) successToast(response.data.message);
     const result = await getWhiteLabelCustomerInfo();
      if (result.status) {
        store.dispatch({ type: FETCH_SUB_DOMAIN_DATA, payload: result.data });
        localStorage.setItem('subDomainData', JSON.stringify(result.data));
      } else {
        console.error(result.message);
        store.dispatch({ type: FETCH_SUB_DOMAIN_DATA, payload: result.data });
      }
      await fetchPortalSettings();
    } catch (err) {
      console.error(err);
      errorToast("Update failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleImageRemove = async (fieldName, imageUrl) => {
    try {
      if (imageUrl && imageUrl.startsWith('https://') && !imageUrl.startsWith('data:')) {
        const { status, message } = await deleteImageFromServer(imageUrl);
        if (status) successToast(message);
        else throw new Error(message);
      }

      setFormValues(prev => ({ ...prev, [fieldName]: null }));
      form.setFieldValue(fieldName, null);

    } catch (error) {
      console.error('Error removing image:', error);
      errorToast(error.message || 'Failed to remove image');
    }
  };
  
  const handleColorChange = (colorType, color) => {
    if (colorType === 'primary') {
      setPrimaryColor(color);
      form.setFieldValue('themeColor', color);
    } else {
      setSecondaryColor(color);
      form.setFieldValue('secondaryColor', color);
    }
  };

  const handleReset = () => {
    setFormValues(originalValues);
    setPrimaryColor(originalValues.themeColor);
    setSecondaryColor(originalValues.secondaryColor);
    form.setFieldsValue(originalValues);
    message.info('Form reset to original values');
  };

  const handleColorPicker = () => {
    setShowPrimaryPicker(true);
  };

  if (dataLoading) {
    return (
      <Card className={styles.loadingCard}>
        <div className={styles.loadingState}>Loading portal settings...</div>
      </Card>
    );
  }

  return (
    <Card
      title="Portal Branding Settings"
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchPortalSettings}
          disabled={loading}
        >
          Refresh
        </Button>
      }
      className={`${styles.mainCard} portal-management-card`}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={formValues}
        onFinish={handleFinish}
      >
        <Row className={`${styles.formRow} portal-management-card-row`} gutter={[24, 24]}>
          <Col span={24} className={`${styles.formCol}`}>
            <Form.Item
              label="Login Page Logo"
              name="loginPageLogo"
              className={`${styles.formItem} w-100 align-items-center`}
            >
              <ImageUpload
                value={formValues.loginPageLogo}
                onChange={(img) => handleImageChange('loginPageLogo', img)}
                onRemove={() => handleImageRemove('loginPageLogo', formValues.loginPageLogo)}
                label="Upload Login Logo"
              />
            </Form.Item>
          </Col>

          <Col span={24} className={`${styles.formCol}`}>
            <Form.Item
              label="Sidebar Logo"
              name="sideBarLogo"
              className={`${styles.formItem} w-100 align-items-center`}
            >
              <ImageUpload
                value={formValues.sideBarLogo}
                onChange={(img) => handleImageChange('sideBarLogo', img)}
                onRemove={() => handleImageRemove('sideBarLogo', formValues.sideBarLogo)}
                label="Upload Sidebar Logo"
              />
            </Form.Item>
          </Col>

          <Col span={24} className={`${styles.formCol}`}>
            <Form.Item
              label="Favicon"
              name="favIcon"
              className={`${styles.formItem} w-100 align-items-center`}
            >
              <ImageUpload
                value={formValues.favIcon}
                onChange={(img) => handleImageChange('favIcon', img)}
                onRemove={() => handleImageRemove('favIcon', formValues.favIcon)}
                label="Upload Favicon"
              />
            </Form.Item>
          </Col>

          <Col span={24} className={styles.formCol}>
            <Form.Item
              label="Login Page Tagline"
              name="loginPageTagLine"
              className={styles.formItem}
              rules={[{ required: true, message: "Please enter a tagline" }]}
            >
              <Input
                className={styles.inputField}
                placeholder="Enter tagline"
                maxLength={100}
                value={formValues.loginPageTagLine}
                onChange={(e) => setFormValues(prev => ({
                  ...prev,
                  loginPageTagLine: e.target.value
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12} className={`${styles.formCol} ${styles.colorPickerCol}`}>
            <Form.Item
              label="Primary Theme Color"
              name="themeColor"
              className={styles.formItem}
              rules={[{ required: true, message: "Please select a color" }]}
            >
              <Input
                value={primaryColor}
                readOnly
                className={`${styles.primaryColorInput} ${styles.colorInput}`}
                style={{ backgroundColor: primaryColor }}
                addonAfter={
                  <Button
                    icon={<BgColorsOutlined />}
                    onClick={handleColorPicker}
                    className={styles.actionButton}
                  />
                }
              />
            </Form.Item>
          </Col>

          <Col span={12} className={`${styles.formCol} ${styles.colorPickerCol}`}>
            <Form.Item
              label="Secondary Theme Color"
              name="secondaryColor"
              className={styles.formItem}
              rules={[{ required: true, message: "Please select a color" }]}
            >
              <Input
                value={secondaryColor}
                readOnly
                className={`${styles.primaryColorInput} ${styles.colorInput}`}
                style={{ backgroundColor: secondaryColor }}
                addonAfter={
                  <Button
                    icon={<BgColorsOutlined />}
                    onClick={() => setShowSecondaryPicker(true)}
                    className={styles.actionButton}
                  />
                }
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item className={styles.buttonRow}>
          <Row gutter={16} className={styles.formRow}>
            <Col span={12} className={styles.buttonCol}>
              <Button
                type="default"
                size="large"
                block
                onClick={handleReset}
                disabled={loading}
                className={styles.actionButton}
              >
                Reset Changes
              </Button>
            </Col>
            <Col span={12} className={styles.buttonCol}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                className={`${styles.container} standard-btn ${styles.actionButton}`}
                block
                loading={loading}
                icon={<UploadOutlined />}
              >
                Update Portal Settings
              </Button>
            </Col>
          </Row>
        </Form.Item>
        {showPrimaryPicker && (
          <ColorPickerModal
            color={primaryColor}
            onChange={(color) => handleColorChange('primary', color)}
            onClose={() => setShowPrimaryPicker(false)}
            visible={showPrimaryPicker}
          />
        )}

        {showSecondaryPicker && (
          <ColorPickerModal
            color={secondaryColor}
            onChange={(color) => handleColorChange('secondary', color)}
            onClose={() => setShowSecondaryPicker(false)}
            visible={showSecondaryPicker}
          />
        )}
      </Form>

    </Card>
  );
};

export default PortalManagementForm;
